import { useState, useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import { useBorrowersQuery } from "./queries";
import { Borrower, BorrowerFilters } from "@/types/borrower";
import { RootState } from "@/store/store";
import { UserRoles } from "@/types/auth";
import {
  parseSearchQuery,
  extractQualifier,
  injectQualifier,
  removeQualifier,
  soleQualifierValue,
} from "@/utils/searchQueryParser";

const DEFAULT_PAGE_SIZE = 20;
const PAGE_SIZE_STORAGE_KEY = "borrowersPageSize";
const PAGE_SIZE_OPTIONS = [10, 20, 50, 100] as const;

export type BorrowerSortColumn =
  | "company_name"
  | "broker_email"
  | "underwriter_email"
  | "created_at"
  | "last_status_changed_at";
export type BorrowerSortDirection = "asc" | "desc";

/** Maps UI column keys to the backend OrderingFilter fields. */
const ORDERING_FIELD_BY_COLUMN: Record<BorrowerSortColumn, string> = {
  company_name: "company_name",
  broker_email: "broker__email",
  underwriter_email: "underwriter__email",
  created_at: "created_at",
  last_status_changed_at: "last_status_changed_at",
};

function getInitialPageSize(): number {
  const stored = window.localStorage.getItem(PAGE_SIZE_STORAGE_KEY);
  const parsed = Number(stored);

  return PAGE_SIZE_OPTIONS.includes(parsed as (typeof PAGE_SIZE_OPTIONS)[number])
    ? parsed
    : DEFAULT_PAGE_SIZE;
}

interface PaginationState {
  count: number;
  totalPages: number;
  currentPage: number;
  pageSize: number;
}

interface UseBorrowersListReturn {
  borrowers: Borrower[];
  loading: boolean;
  pagination: PaginationState;
  filters: BorrowerFilters;
  /** The Status select's value: the search's one included status, else "" (all). */
  statusFilterValue: string;
  rawSearch: string;
  setRawSearch: (value: string) => void;
  handleStatusSelect: (status: string) => void;
  page: number;
  setPage: (page: number) => void;
  pageSize: number;
  setPageSize: (size: number) => void;
  sortColumn: BorrowerSortColumn | null;
  sortDirection: BorrowerSortDirection;
  handleSortClick: (column: BorrowerSortColumn) => void;
}

/** Apply active-org switcher filter when search has no explicit org: qualifier. */
export function applyActiveOrganizationFilter(
  filters: BorrowerFilters,
  rawSearch: string,
  activeOrganizationId: number | null,
  role: UserRoles | undefined,
  organizationCount: number
): BorrowerFilters {
  if (extractQualifier(rawSearch, "org") || filters.organization_name) {
    return filters;
  }
  if (!activeOrganizationId) {
    return filters;
  }
  if (role !== "broker" && role !== "underwriter") {
    return filters;
  }
  if (organizationCount < 2) {
    return filters;
  }
  if (filters.organization_id) {
    return filters;
  }
  return { ...filters, organization_id: activeOrganizationId };
}

export const useBorrowersList = (enabled = true): UseBorrowersListReturn => {
  const [rawSearch, setRawSearchState] = useState("");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSizeState] = useState(getInitialPageSize);
  const [sortColumn, setSortColumn] = useState<BorrowerSortColumn | null>(null);
  const [sortDirection, setSortDirection] = useState<BorrowerSortDirection>("asc");
  const pageNum = page + 1;

  const user = useSelector((state: RootState) => state.auth.user);
  const activeOrganizationId = useSelector((state: RootState) => state.auth.activeOrganizationId);

  const setPageSize = useCallback((size: number) => {
    window.localStorage.setItem(PAGE_SIZE_STORAGE_KEY, String(size));
    setPageSizeState(size);
    setPage(0);
  }, []);

  const setRawSearch = useCallback((value: string) => {
    setRawSearchState(value);
    setPage(0);
  }, []);

  const handleStatusSelect = useCallback((status: string) => {
    setRawSearchState((prev) => {
      const cleared = removeQualifier(prev, "status");
      if (!status) return cleared;
      return injectQualifier(cleared, "status", status);
    });
    setPage(0);
  }, []);

  const handleSortClick = useCallback(
    (column: BorrowerSortColumn) => {
      setPage(0);
      if (sortColumn !== column) {
        setSortColumn(column);
        setSortDirection("asc");
        return;
      }
      if (sortDirection === "asc") {
        setSortDirection("desc");
        return;
      }
      setSortColumn(null);
      setSortDirection("asc");
    },
    [sortColumn, sortDirection]
  );

  const parsed = useMemo(() => parseSearchQuery(rawSearch), [rawSearch]);

  const filters: BorrowerFilters = useMemo(() => {
    const base = Object.fromEntries(
      Object.entries(parsed).filter(([, v]) => v !== "" && v !== undefined)
    ) as BorrowerFilters;
    return applyActiveOrganizationFilter(
      base,
      rawSearch,
      activeOrganizationId,
      user?.role,
      user?.organizations?.length ?? 0
    );
  }, [parsed, rawSearch, activeOrganizationId, user?.role, user?.organizations?.length]);

  const ordering = useMemo(() => {
    if (!sortColumn) return undefined;
    const field = ORDERING_FIELD_BY_COLUMN[sortColumn];
    return sortDirection === "desc" ? `-${field}` : field;
  }, [sortColumn, sortDirection]);

  const queryFilters: BorrowerFilters = useMemo(
    () => (ordering ? { ...filters, ordering } : filters),
    [filters, ordering]
  );

  const { data, isLoading } = useBorrowersQuery(queryFilters, pageNum, pageSize, enabled);

  const statusFilterValue = useMemo(
    () => soleQualifierValue(rawSearch, "status")?.toLowerCase() ?? "",
    [rawSearch]
  );

  const borrowers = data?.results ?? [];
  const loading = isLoading;
  const pagination: PaginationState = data
    ? {
        count: data.count,
        totalPages: data.total_pages,
        currentPage: data.current_page,
        pageSize: data.page_size,
      }
    : {
        count: 0,
        totalPages: 0,
        currentPage: 1,
        pageSize,
      };

  return {
    borrowers,
    loading,
    pagination,
    filters,
    statusFilterValue,
    rawSearch,
    setRawSearch,
    handleStatusSelect,
    page,
    setPage,
    pageSize,
    setPageSize,
    sortColumn,
    sortDirection,
    handleSortClick,
  };
};
